import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppConfigService } from '@app/app-config.service';
import { Page } from '@app/shared/models/page.interface';
import { License, LicenseDto, LicenseHistory, Profile, ProfileLicense } from '@app/shared/models/user.interface';
import { DataTablePageState } from '@components/data/data-table-new/directives/datatable-pagination.directive';
import { DataTableSortState } from '@components/data/data-table-new/directives/datatable-sort.directive';
import { Observable } from 'rxjs';
import { ParameterResolutionService } from '../parameter/parameter.service';

@Injectable({
  providedIn: 'root'
})
export class LicenseService {
  protected warningExpireTime = 15;

  constructor(
    protected http: HttpClient,
    private appConfigService: AppConfigService,
    protected parameterResolutionService: ParameterResolutionService
  ) {
    this.warningExpireTime = this.parameterResolutionService.resolveNumericValue('USR_LICENSE_WARNING_EXPIRED_TIME');
    if (isNaN(this.warningExpireTime)) {
      this.warningExpireTime = 15;
    }
  }

  getAll(): Observable<License[]> {
    return this.http.get<License[]>(`${this.appConfigService.getApi()}/licenses/all`);
  }

  getByName(name: string): Observable<License> {
    return this.http.get<License>(`${this.appConfigService.getApi()}/licenses/name/${name}`);
  }

  /**
   * Check if an license triggers a warning because is near to be out of date
   *
   * @param element an instance of LicenseDto
   * @return true if the condition is true
   */
  waningOnRemaining(element: LicenseDto): boolean {
    return element.remaining < this.warningExpireTime;
  }

  /**
   * Check if an license triggers a warning because exceeds the number
   *
   * @param element an instance of LicenseDto
   * @return true if the condition is true
   */
  exceedsLicenses(element: LicenseDto): boolean {
    return element.available < 0;
  }

  /**
   * Get all the available licenses
   */
  getAllPaginated(
    endpoint: string,
    page: DataTablePageState,
    sort: DataTableSortState,
    filter: string
  ): Observable<Page<LicenseDto>> {
    let params = new HttpParams();
    params = params.append('filter', filter);
    params = params.append('page', page.index.toString());
    params = params.append('size', page.size.toString());
    params = params.append('sort', sort.column + ',' + sort.direction);

    return this.http.get<Page<LicenseDto>>(this.appConfigService.getApi() + endpoint, {
      params
    });
  }

  /**
   * Get license last control
   *
   * @return an Observable of LicenseHistory
   */
  getLicenseLastControl(): Observable<LicenseHistory> {
    return this.http.get<LicenseHistory>(`${this.appConfigService.getApi()}/licenses/lastcontrol`);
  }

  /**
   * Get the license name
   *
   * @param profile an instance of Profile
   * @return the name
   */
  getLicenseName(profile: Profile): string {
    var name = '';
    if (profile.profileLicenses) {
      this.sortLicensesByName(profile).forEach(profileLicense => {
        if (name != '') {
          name += ',';
        }
        name += profileLicense.license.name;
      });
    }
    return name;
  }

  /**
   * Sort profile licenses by name
   *
   * @param profile profile
   * @return a sorted list of ProfileLicense
   */
  sortLicensesByName(profile: Profile): ProfileLicense[] {
    return profile.profileLicenses.sort((a, b) => {
      return a.license.name.localeCompare(b.license.name);
    });
  }

  /**
   * Get the available licenses
   *
   * @param profile an instance of Profile
   * @return the available licenses
   */
  getAvailableLicenses(profile: Profile): number {
    var available = 0;
    if (profile.profileLicenses) {
      // the available licenses is the min value of all
      var min = 1000000;
      profile.profileLicenses.forEach(profileLicense => {
        let value = profileLicense.license.available || 0;
        if (value < min) {
          min = value;
        }
      });
      available = min;
    }
    return available;
  }

  /**
   * Get the license to date value
   *
   * @param profile profile
   * @return the value
   */
  getLicenseToDate(profile: Profile): string {
    var toDate = '';
    if (profile.profileLicenses) {
      var minDate = new Date(2050, 12, 31);
      profile.profileLicenses.forEach(profileLicense => {
        toDate = profileLicense.license.toDate;
        if (!toDate) {
          toDate = new Date().toUTCString();
        }
        if (new Date(toDate) < minDate) {
          minDate = new Date(toDate);
        }
      });
      toDate = minDate.toString();
    }
    return toDate;
  }

  /**
   * Convert a list of license ids to a list of ProfileLicense instances
   *
   * @param licenseIds license ids
   * @param profile profile
   */
  fromLicenseIdsToProfileLicenses(licenseIds: string[], profile: Profile): ProfileLicense[] {
    let newProfileLicenses = [];
    licenseIds.forEach(licid => {
      let profileLicense = profile.profileLicenses.find(
        profileLicenseToFind => profileLicenseToFind.license.id === licid
      );
      if (profileLicense) {
        // The license already exists... just push it
        newProfileLicenses.push(profileLicense);
      } else {
        // New license for this profile
        newProfileLicenses.push({ id: null, profile: profile.id, license: { id: licid } });
      }
    });
    return newProfileLicenses;
  }
}
